import { Archive, ArrowLeft, ArrowRightCircle, ExternalLink, Pencil, RotateCcw } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DetailColumns } from '@/components/ui/detail-columns';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import { AuditTimelinePanel } from '@/features/audit';
import { PurgeArchivedButton } from '@/features/crm/components/PurgeArchivedButton';
import {
  bidStatusLabel,
  bidStatusVariant,
  channelLabel,
  jobTypeLabel,
  originLabel,
  vsMedianLabel,
} from '../constants';
import { formatDateTime } from '@/lib/datetime';
import { BidInsights } from '../components/BidInsights';
import { bidTitle, formatBidValue, formatRate } from '../format';
import { useArchiveBid, useBid, useConvertBid, usePurgeBid, useRestoreBid } from '../queries';
import type { BidFunnel } from '../types';

export function BidDetailPage() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();
  const { data: bid, isPending, isError, error } = useBid(publicId);
  const archive = useArchiveBid();
  const restore = useRestoreBid();
  const purge = usePurgeBid();
  const convert = useConvertBid();

  async function handleConvert() {
    if (!bid) return;
    const lead = await convert.mutateAsync(publicId);
    navigate(`/crm/leads/${lead.public_id}`);
  }

  if (isPending) {
    return (
      <div className="max-w-2xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/crm/bids')}>
          <ArrowLeft /> Back
        </Button>
        <p className="text-destructive">{(error as unknown as ApiError).message}</p>
      </div>
    );
  }

  const title = bidTitle(bid);
  const edited = bid.human_edited_fields.length;

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/crm/bids')} aria-label="Back">
            <ArrowLeft />
          </Button>
          <h1 className="truncate text-2xl font-semibold tracking-tight">{title}</h1>
          <Badge variant={bidStatusVariant(bid.status)}>{bidStatusLabel(bid.status)}</Badge>
        </div>
        {/* Archived rows only restore/purge — the backend admits them for reads alone. */}
        {bid.is_archived ? null : (
        <div className="flex gap-2">
          {bid.lead_public_id ? (
            // One-way, once: after conversion the button becomes the link.
            <Button asChild variant="secondary">
              <Link to={`/crm/leads/${bid.lead_public_id}`}>
                <ArrowRightCircle /> Open lead
              </Link>
            </Button>
          ) : (
            <Button onClick={() => void handleConvert()} disabled={convert.isPending}>
              <ArrowRightCircle /> {convert.isPending ? 'Converting…' : 'Convert to lead'}
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate(`/crm/bids/${bid.public_id}/edit`)}>
            <Pencil /> Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" aria-label="Archive bid">
                <Archive /> Archive
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Archive this bid?</AlertDialogTitle>
                <AlertDialogDescription>
                  Hide it from the ledger and the roll-up. You can restore it from Archived view.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => archive.mutate(publicId, { onSuccess: () => navigate('/crm/bids') })}
                  disabled={archive.isPending}
                >
                  {archive.isPending ? 'Archiving…' : 'Archive'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
        )}
      </div>

      {bid.is_archived ? (
        <Card className="bg-muted/40 border-dashed">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
            <p className="text-sm font-medium">This bid is archived.</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => restore.mutate(publicId)} disabled={restore.isPending}>
                <RotateCcw /> {restore.isPending ? 'Restoring…' : 'Restore'}
              </Button>
              <PurgeArchivedButton
                variant="full"
                name={title}
                onPurge={() => purge.mutate(publicId, { onSuccess: () => navigate('/crm/bids') })}
                pending={purge.isPending}
              />
            </div>
          </CardContent>
        </Card>
      ) : null}

      <DetailColumns
        main={
          <>
            <Card>
              <CardHeader>
                <CardTitle>Proposal</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <Field label="Channel">{channelLabel(bid.channel)}</Field>
                <Field label="Origin">
                  {originLabel(bid.origin)}
                  {edited > 0 ? (
                    <span className="text-muted-foreground ml-1 text-xs">
                      · {edited} field{edited === 1 ? '' : 's'} edited by hand
                    </span>
                  ) : null}
                </Field>
                <Field label="Owner (bidder)">
                  {bid.owner_name ?? '—'}
                  {bid.bidder_profile ? (
                    <span className="text-muted-foreground"> · {bid.bidder_profile}</span>
                  ) : null}
                </Field>
                <Field label="Job id">{bid.job_id}</Field>
                <Field label="Proposal id">{bid.proposal_id || '—'}</Field>
                <Field label="Submitted">{formatDateTime(bid.submitted_at)}</Field>
                <Field label="Job">
                  {bid.job_url ? (
                    <a
                      href={bid.job_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 underline underline-offset-2"
                    >
                      {bid.job_title || bid.job_url} <ExternalLink className="size-3" />
                    </a>
                  ) : (
                    bid.job_title || '—'
                  )}
                </Field>
                <Field label="Job type">{bid.job_type ? jobTypeLabel(bid.job_type) : '—'}</Field>
                <Field label="Client budget">{bid.client_budget || '—'}</Field>
                <Field label="Experience level">{bid.experience_level || '—'}</Field>
                <Field label="Posted">{formatDateTime(bid.posted_at)}</Field>
                <Field label="Connects cost">{bid.connects_cost ?? '—'}</Field>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Our bid vs the job</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <Field label="Our bid">
                  {formatBidValue(bid)}
                  {bid.bid_vs_median ? (
                    <Badge variant="outline" className="ml-2">
                      {vsMedianLabel(bid.bid_vs_median)}
                    </Badge>
                  ) : null}
                </Field>
                <Field label="Profile rate at submit">
                  {formatRate(bid.profile_rate_at_submit, bid.currency, 'hourly')}
                </Field>
                <Field label="Boosted">{bid.boosted ? 'Yes' : 'No'}</Field>
                <Field label="Job bid range (at submit)">
                  {bid.bid_range_low || bid.bid_range_median || bid.bid_range_high
                    ? `${formatRate(bid.bid_range_low, bid.currency, bid.job_type)} · ${formatRate(
                        bid.bid_range_median,
                        bid.currency,
                        bid.job_type,
                      )} · ${formatRate(bid.bid_range_high, bid.currency, bid.job_type)}`
                    : '—'}
                </Field>
                <Field label="Connects">
                  {bid.connects_spent ?? '—'} spent
                  {bid.connects_refunded ? ` · ${bid.connects_refunded} refunded` : ''}
                </Field>
                <Field label="Bid-filter verdict" className="sm:col-span-2 lg:col-span-3">
                  {bid.bid_filter_verdict || '—'}
                </Field>
              </CardContent>
            </Card>

            <div className="grid gap-4 md:grid-cols-2">
              <FunnelCard title="Job funnel · at submit" funnel={bid.funnel_at_submit} />
              <FunnelCard title="Job funnel · latest" funnel={bid.funnel_latest} />
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Client</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <Field label="Country">{bid.client_country || '—'}</Field>
                <Field label="Member since">
                  {bid.client_member_since ? new Date(bid.client_member_since).toLocaleDateString() : '—'}
                </Field>
                <Field label="Verified">
                  {yesNo(bid.client_payment_verified, 'payment')} · {yesNo(bid.client_phone_verified, 'phone')}
                </Field>
                <Field label="Jobs posted / hires">
                  {bid.client_jobs_posted ?? '—'} / {bid.client_hires ?? '—'}
                  {bid.client_hire_rate_pct ? ` (${Number(bid.client_hire_rate_pct)}% hire rate)` : ''}
                </Field>
                <Field label="Open jobs">{bid.client_open_jobs ?? '—'}</Field>
                <Field label="Total spent">
                  {formatRate(bid.client_total_spent, bid.currency, 'fixed')}
                  {bid.client_hourly_band ? ` · ${bid.client_hourly_band}` : ''}
                </Field>
                <Field label="Feedback score">{bid.client_feedback_score ?? '—'}</Field>
              </CardContent>
            </Card>
          </>
        }
        aside={
          <>
            <Card>
              <CardHeader>
                <CardTitle>Outcome</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4">
                <Field label="Status">
                  <Badge variant={bidStatusVariant(bid.status)}>{bidStatusLabel(bid.status)}</Badge>
                </Field>
                <Field label="Viewed by client">
                  {bid.viewed_by_client ? formatDateTime(bid.viewed_at) : 'No'}
                </Field>
                <Field label="Replied">{formatDateTime(bid.replied_at)}</Field>
                <Field label="Interview room">{bid.room_id || '—'}</Field>
                <Field label="Contract">{bid.contract_id || '—'}</Field>
                <Field label="Closed">{formatDateTime(bid.closed_at)}</Field>
                <Field label="Lead">
                  {bid.lead_public_id ? (
                    <Link to={`/crm/leads/${bid.lead_public_id}`} className="underline underline-offset-2">
                      {bid.lead_title ?? 'Open lead'}
                    </Link>
                  ) : (
                    'Not converted'
                  )}
                  {bid.converted_by !== 'none' ? (
                    <span className="text-muted-foreground"> · by {bid.converted_by}</span>
                  ) : null}
                </Field>
                <Field label="Latest snapshot">{formatDateTime(bid.snapshot_at)}</Field>
              </CardContent>
            </Card>

            {bid.insights ? (
              <Card>
                <CardHeader>
                  <CardTitle>Insights</CardTitle>
                </CardHeader>
                <CardContent>
                  <BidInsights value={bid.insights} currency={bid.currency || 'USD'} />
                </CardContent>
              </Card>
            ) : null}

            <AuditTimelinePanel entity="crm.bid" entityId={bid.id} />
          </>
        }
      />
    </div>
  );
}

function yesNo(v: boolean | null, what: string) {
  return v === null ? `${what} ?` : v ? `${what} ✓` : `${what} ✗`;
}

function FunnelCard({ title, funnel }: { title: string; funnel: BidFunnel }) {
  const empty = !funnel || Object.keys(funnel).length === 0;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-3">
        {empty ? (
          <p className="text-muted-foreground col-span-2 text-sm">No snapshot yet.</p>
        ) : (
          <>
            <Field label="Proposals">{funnel.job_proposals ?? '—'}</Field>
            <Field label="Invites sent">{funnel.job_invites_sent ?? '—'}</Field>
            <Field label="Interviewing">{funnel.job_interviewing ?? '—'}</Field>
            <Field label="Offers">{funnel.job_offers ?? '—'}</Field>
            <Field label="Hires">{funnel.job_hires ?? '—'}</Field>
            <Field label="Client last viewed">{formatDateTime(funnel.job_last_viewed_by_client_at)}</Field>
            <Field label="Read at" className="col-span-2">
              {formatDateTime(funnel.snapshot_at)}
            </Field>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className="text-muted-foreground text-xs font-medium uppercase">{label}</p>
      <div className="mt-1 text-sm break-words whitespace-pre-wrap">{children}</div>
    </div>
  );
}

